/**
 * Word List Localization Types
 *
 * TypeScript interfaces for word list localization (translating list name/description
 * to user's native language for display purposes).
 */

// =============================================================================
// Frontend Types (camelCase)
// =============================================================================

export interface WordListLocalization {
  wordListId: string;
  languageCode: string;
  localizedName: string;
  localizedDescription: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WordListLocalizationCreate {
  languageCode: string;
  localizedName: string;
  localizedDescription?: string | null;
}

export interface WordListLocalizationUpdate {
  localizedName?: string;
  localizedDescription?: string | null;
}

export interface GenerateLocalizationsRequest {
  targetLanguages: string[];
}

export interface GeneratedLocalization {
  languageCode: string;
  localizedName: string;
  localizedDescription: string | null;
}

// =============================================================================
// API Response Types (snake_case from backend)
// =============================================================================

export interface WordListLocalizationApiResponse {
  word_list_id: string;
  language_code: string;
  localized_name: string;
  localized_description: string | null;
  created_at: string;
  updated_at: string;
}

export interface GeneratedLocalizationApiResponse {
  language_code: string;
  localized_name: string;
  localized_description: string | null;
}

export interface GenerateLocalizationsApiResponse {
  localizations: GeneratedLocalizationApiResponse[];
}

// =============================================================================
// Mapping Functions
// =============================================================================

export function mapLocalizationFromApi(api: WordListLocalizationApiResponse): WordListLocalization {
  return {
    wordListId: api.word_list_id,
    languageCode: api.language_code,
    localizedName: api.localized_name,
    localizedDescription: api.localized_description,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

export function mapGeneratedLocalizationFromApi(
  api: GeneratedLocalizationApiResponse
): GeneratedLocalization {
  return {
    languageCode: api.language_code,
    localizedName: api.localized_name,
    localizedDescription: api.localized_description,
  };
}

export function mapLocalizationCreateToApi(
  data: WordListLocalizationCreate
): Record<string, unknown> {
  return {
    language_code: data.languageCode,
    localized_name: data.localizedName,
    localized_description: data.localizedDescription ?? null,
  };
}

export function mapLocalizationUpdateToApi(
  data: WordListLocalizationUpdate
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (data.localizedName !== undefined) {
    result.localized_name = data.localizedName;
  }
  if (data.localizedDescription !== undefined) {
    result.localized_description = data.localizedDescription;
  }
  return result;
}
